import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import PageWrapper from "@components/Home/PageWrapper";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import SequenceRecall from "@components/Home/Games/SequenceRecall";
import NBack from "@components/Home/Games/NBack";
import WordFluencyTap from "@components/Home/Games/WordFluencyTap";
import MathBlitz from "@components/Home/Games/MathBlitz";
import PathMemory from "@components/Home/Games/PathMemory";
import ReactionTap from "@components/Home/Games/ReactionTap";
import CategoryTap from "@components/Home/Games/CategoryTap";
import PatternMatch from "@components/Home/Games/PatternMatch";
import RuleSwitch from "@components/Home/Games/RuleSwitch";
import TargetFocus from "@components/Home/Games/TargetFocus";
import SessionSummary from "@components/Home/Games/SessionSummary";
import type { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import { useCompleteSessionMutation } from "@store/gameApi";

const REST_SECONDS = 15;

export interface GuidedPlayFlowLocationState {
  sessionId: string;
  sessionType: string;
  games: GameSessionGame[];
}

const GAME_ID_TO_COMPONENT: Record<
  string,
  FC<{
    sessionId: string;
    currentGame: GameSessionGame;
    gameIndex: number;
    totalGames: number;
    onGameComplete: () => void;
  }>
> = {
  [GAME_IDS.sequenceRecall]: SequenceRecallGuidedWrapper,
  [GAME_IDS.nBack]: NBackGuidedWrapper,
  [GAME_IDS.wordFluencyTap]: WordFluencyTapGuidedWrapper,
  [GAME_IDS.mathBlitz]: MathBlitzGuidedWrapper,
  [GAME_IDS.pathMemory]: PathMemoryGuidedWrapper,
  [GAME_IDS.reactionTap]: ReactionTapGuidedWrapper,
  [GAME_IDS.categoryTap]: CategoryTapGuidedWrapper,
  [GAME_IDS.patternMatch]: PatternMatchGuidedWrapper,
  [GAME_IDS.ruleSwitch]: RuleSwitchGuidedWrapper,
  [GAME_IDS.targetFocus]: TargetFocusGuidedWrapper,
};

function SequenceRecallGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <SequenceRecall
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function NBackGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <NBack
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function WordFluencyTapGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <WordFluencyTap
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function TargetFocusGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <TargetFocus
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function CategoryTapGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <CategoryTap
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function MathBlitzGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <MathBlitz
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function PathMemoryGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <PathMemory
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function ReactionTapGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <ReactionTap
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function PatternMatchGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <PatternMatch
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

function RuleSwitchGuidedWrapper({
  sessionId,
  currentGame,
  gameIndex,
  totalGames,
  onGameComplete,
}: {
  sessionId: string;
  currentGame: GameSessionGame;
  gameIndex: number;
  totalGames: number;
  onGameComplete: () => void;
}) {
  return (
    <RuleSwitch
      guidedSessionId={sessionId}
      guidedCurrentGame={currentGame}
      guidedGameIndex={gameIndex}
      guidedTotalGames={totalGames}
      onGuidedGameComplete={onGameComplete}
    />
  );
}

const GuidedPlayFlow: FC<{ basePath?: string }> = ({ basePath = "" }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as GuidedPlayFlowLocationState | null;
  const [completeSession] = useCompleteSessionMutation();

  const [gameIndex, setGameIndex] = useState(0);
  const [restCountdown, setRestCountdown] = useState<number | null>(null);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [domainUpdatesSum] = useState<Record<string, number>>({});

  const prefix = basePath ? `/${basePath}` : "";
  const sessionId = state?.sessionId;
  const games = state?.games ?? [];

  const handleGameComplete = useCallback(() => {
    if (gameIndex >= games.length - 1) {
      completeSession({ sessionId: sessionId! })
        .unwrap()
        .then((res) => {
          setSummaryStats({
            accuracyPercent: 0,
            avgReactionSeconds: 0,
            xpEarned: res.totalXp,
            domainChanges: domainUpdatesSum,
          });
        })
        .catch(() => {
          setSummaryStats({
            accuracyPercent: 0,
            avgReactionSeconds: 0,
            xpEarned: 0,
            domainChanges: domainUpdatesSum,
          });
        });
      return;
    }
    setRestCountdown(REST_SECONDS);
  }, [gameIndex, games.length, sessionId, completeSession, domainUpdatesSum]);

  React.useEffect(() => {
    if (restCountdown === null || restCountdown <= 0) return;
    const t = setTimeout(() => setRestCountdown(restCountdown - 1), 1000);
    return () => clearTimeout(t);
  }, [restCountdown]);

  const handleRestReady = () => {
    setRestCountdown(null);
    setGameIndex((i) => i + 1);
  };

  const handleSummaryContinue = () => {
    navigate(prefix ? prefix : "/");
  };

  if (!state?.sessionId || !games.length) {
    return (
      <PageWrapper>
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">
            No session in progress. Start from the home screen.
          </Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate(prefix ? prefix : "/")}>
            Go back
          </Button>
        </Box>
      </PageWrapper>
    );
  }

  if (summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  if (restCountdown !== null) {
    return (
      <PageWrapper>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={6}
          px={2}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t("games.guided.restTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t("games.guided.restSubtitle")}
          </Typography>
          <Typography variant="h4" color="primary.main" sx={{ mb: 2 }}>
            {restCountdown}s
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleRestReady}
            sx={{ minHeight: 72 }}
          >
            {t("games.guided.ready")}
          </Button>
        </Box>
      </PageWrapper>
    );
  }

  const currentGame = games[gameIndex];
  const GameComponent = GAME_ID_TO_COMPONENT[currentGame?.gameId];

  if (!currentGame || !GameComponent) {
    return (
      <PageWrapper>
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">
            Game &quot;{currentGame?.gameId ?? "unknown"}&quot; is not available yet.
          </Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate(prefix || "/")}>
            Go back
          </Button>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <GameComponent
      sessionId={sessionId!}
      currentGame={currentGame}
      gameIndex={gameIndex}
      totalGames={games.length}
      onGameComplete={handleGameComplete}
    />
  );
};

export default GuidedPlayFlow;
